import {
    isLocalHost,
    isProdHost,
    isStageHost,
    readLocation,
} from './env.config';
import { VendingMachineTypeId } from './types';

const LOCATION = readLocation();

export const IS_DEV = import.meta.env.DEV;
export const IS_LOCAL_DEVELOPMENT = isLocalHost(LOCATION.origin);

/** App-level routes that are not parameterized by a vending machine ID. */
export const APP_ROUTES = {
    home: '/',
    sessionExpired: 'session-expired',
    devAnimations: 'dev/animations',
    devGame: 'dev/game',
    notFound: '*',
} as const;

export const IS_STAGE = isStageHost(LOCATION.host);
// Substring match — `true` on both pay.prostopay.net (prod) and
// qa.pay.prostopay.net (QA), which is intentional: QA uses the prod database.
export const IS_PROD = isProdHost(LOCATION.hostname);

export const DEFAULT_LANGUAGE_KEY = 'en';
export const SUPPORTED_LANGUAGES = [
    'uk',
    'en',
    'ka',
    'it',
    'ro',
    'bg',
    'de',
    'es',
];

/**
 *
"1","Снековий автомат"
"2","Кавовий автомат"
"3","Спарка (мастер - снек)"
"4","Спарка (мастер - кава)"
"5","Кавомашина"
"6","Фрешевий"
"7","Морозиво"
"8","Цигарки"
"9","Кондомат"
"10","Холодильник"
"11","Розлив води - пульс"
"12","Снековий - пульс"
"13","Холодильник Смарт"
 */
// export const TAB_ID_STORAGE_KEY = 'tbbd';
export const TAB_SYNC_DURATION = 5000;

export const STORAGE_RENEW_INTERVAL = 24 * 3600000; // 1 day

export const STORAGE_KEYS = ((canReadKeys = IS_DEV || IS_STAGE) => {
    return {
        VM_STATE: canReadKeys ? 'vm_state' : 'vms',
        VM_SYNC_TIMESTAMP: canReadKeys ? 'vm_sync_timestamp' : 'vmst',
        TAB_ID: canReadKeys ? 'current_tab_id' : 'tbbd',
        TAB_START_TIMESTAMP: canReadKeys ? 'start' : '5T@t',
        ORDER_STATE: canReadKeys ? 'order_state' : 'orsTt',
        ORDER_SYNC_TIMESTAMP: canReadKeys ? 'order_sync_timestamp' : 'or_5t_tm',
        DEBUG_IS_ALLOWED: 'qrDebug',
        APP_VERS: canReadKeys ? 'app_version' : 'VwAp83r51Ya',
        STORE_TIMESTAMP: canReadKeys ? 'store_timestamp' : 'sttR-T1m',
        TIMER_END_TIMESTAMP: canReadKeys ? 'timer_end_timestamp' : 'tmEnD',
        LOYALTY_BANNER_SEEN: canReadKeys ? 'loyalty_banner_seen' : 'lB5n',
    };
})();

export const MACHINE_TYPES = {
    SNACK_MACHINE: '1',
    COFFEE_AUTOMAT: '2',
    SNACK_MACHINE_SPARKA: '3',
    COFFEE_MACHINE_SPARKA: '4',
    COFFEE_MACHINE: '5',
    FRESH_MACHINE: '6',
    ICE_CREAM_MACHINE: '7',
    CIGARETTE_MACHINE: '8',
    CONDOM_MACHINE: '9',
    FRIDGE: '10',
    WATER_MACHINE_PULS: '11',
    SNACK_MACHINE_PULS: '12',
    FRIDGE_SMART: '13',
    PULSE_MACHINE: '14',
    CAR_WASH_MACHINE: '15',
    POPCORN_PULSE_MACHINE: '16',
} as const;

/** Machine types that use direct payment (no product selection), checked in WaitScreen */
export const DIRECT_PAYMENT_MACHINE_TYPES: VendingMachineTypeId[] = [
    MACHINE_TYPES.FRIDGE,
    MACHINE_TYPES.WATER_MACHINE_PULS,
    MACHINE_TYPES.SNACK_MACHINE_PULS,
];

/**
 * Pulse-metered machines: the machine dispenses for the amount paid and reports the
 * sale itself, so there is no product to pick. Kept apart from
 * DIRECT_PAYMENT_MACHINE_TYPES, which guards the CT1 fallbacks in WaitScreen.
 */
export const PULSE_MACHINE_TYPES: VendingMachineTypeId[] = [
    MACHINE_TYPES.WATER_MACHINE_PULS,
    MACHINE_TYPES.SNACK_MACHINE_PULS,
    MACHINE_TYPES.PULSE_MACHINE,
    MACHINE_TYPES.CAR_WASH_MACHINE,
    MACHINE_TYPES.POPCORN_PULSE_MACHINE,
];

export const CT_TYPES = {
    NULL: null, // default for CT1 and CT10
    CT1: 1,
    CT5: 5,
    CT10: 10,
} as const;
/*
`device_status` is a raw mirror of the last `Cashless` command number the machine
sent. It says what the device last reported, never anything about money — that is
`status`'s job. web0 writes it in three places, always as the literal command
number (IncomingController: case 3 → 3, case 4 → 4, case 5/6 → the command).

From Volodymyr Serednii:
  1 - звернення.
  2 не знаю
  3 - вибір продукту, здається,
  4 - підтвердження/скасування продажу (залежить від поля status яке приходить з цим кешлесом),
  5 - ?
  6 - завершення,

Two things worth knowing, both established by tracing web0 (SWRN-1893):

1. `4` is STICKY. Once set, a later `Cashless 6` cannot overwrite it —
   `IncomingController.php:1328` guards on `device_status != 4`, added in 2020 by a
   one-line commit whose message is the whole rationale: "do not reset
   order.device_status=4". So `(x, 4)` versus `(x, 6)` records nothing but arrival
   order: 4 means a vend result arrived at some point, 6 means the session ended
   and one never did.

2. `4` does NOT mean "paid". `case 4` dispatches on the message's own `Status`
   field — `Status 0` is a refund, `Status 1` is a confirmed sale — and both write
   `device_status = 4`. Which is why the naming below (`PAID: 4`) reads as more
   specific than it is.

⚠ A previous version of this comment carried a second list attributed to
https://prostopay.atlassian.net/wiki/spaces/SW/pages/155451432 — it has been
removed because it is not about `device_status` at all. That page's table is headed
`[status]`, and its rows (4 = кошти успішно повернуто, 6 = Відшкодовано вручну) are
`wallet_user_order.status` values, which are documented on ORDER_STATUS above. The
list had been transcribed here under the wrong column name.
  */

/**
 * `wallet_user_order.status`.
 *
 * Meanings verified against web0, which owns every write to this column, and
 * against production row counts (all time, `GROUP BY status`) so nothing here
 * looks dead when it is not:
 *
 *   0 OPENED             862,899   QR scanned, no commit attempted — the natural
 *                                  abandonment funnel, not an error
 *   1 PREPAID             19,140   committed, waiting for the machine to confirm
 *   2 PAYMENT_FAIL        88,705   declined, or device_status_6 already set
 *   3 COMPLETED        3,790,670   paid and vended
 *   4 REFUNDED            34,992   refund succeeded automatically
 *   5 REFUND_FAILED           37   refund attempt FAILED — money not returned
 *   6 REFUNDED_MANUALLY    3,614   refund done by an operator in the back office
 *   7 REFUND_DEFFERED          0   transient, see below
 *
 * `REFUND_FAILED` is a terminal "we still owe the customer" state, which is why
 * there are so few of them — each one is an operational alarm.
 *
 * `REFUNDED_MANUALLY` is Portmone-only: `voidPortmone($manual = true)`, reached
 * from the back-office cabinet, which also stamps `user_rtrn_id` and `date_rtrn`.
 *
 * `REFUND_DEFFERED` having zero rows is correct, not evidence it is unused. A
 * Portmone `reject` only works within 24h; when it fails the order parks at 7 and
 * a scheduled task retries it as a `return` between 24h and 10 days later, moving
 * it to 4 or 5. It is a transient queue state, so a point-in-time count is
 * normally empty. Keep handling it.
 *
 * Status 6 is handled by the wait-screen flows as of SWRN-1900: it resolves to
 * the `refunded-manually` state, which every flow routes to the refund screen.
 * It is the one state that ignores `device_status`, because a manual back-office
 * return is already the whole fact — see `order-state.ts`.
 */
export const ORDER_STATUS = {
    OPENED: 0,
    PREPAID: 1,
    PAYMENT_FAIL: 2,
    COMPLETED: 3,
    REFUNDED: 4,
    REFUND_FAILED: 5,
    REFUNDED_MANUALLY: 6,
    REFUND_DEFFERED: 7,
} as const;

// after order check we have order.device_status
export const DEVICE_STATUS = {
    HAS_CONNECTION: 1,
    PRODUCT_SELECTED: 3,
    PAID: 4,
    CONNECTED: 5,
    COMPLETED: 6,
    NULL: null,
} as const;

// from https://prostopay.atlassian.net/wiki/spaces/SW/pages/155451432/Current+WEB+Payments+details
/**
 * `merchants.provider_id` — which gateway a machine's merchant is set up with.
 *
 * ⚠ This map used to be a copy of a DIFFERENT enum. web0's
 * `WalletUserOrder::payment_system` (0 MasterPass, 1 GooglePay, 2 ApplePay,
 * 3 KaspiKZ, 4 TBC, 5 MyPos, 6 EmerchantPay, 8 Mia) records *how an order was
 * paid*; `merchants.provider_id` selects *which gateway to offer*. They are
 * different columns on different tables and they do not agree.
 *
 * The two happen to coincide for Kaspi (3), MyPOS (5), EMerchantPay (6) and
 * MiaPay (8), which is why the copy went unnoticed — and they disagree for TBC,
 * which is why someone had to bolt on a `TWO: 2` entry with the comment "unclear
 * how to name". That entry was the only correct one for TBC; the inherited
 * `TBC: 4` matched nothing.
 *
 * Verified against production (active machines, joining vm → point_of_sales /
 * company → merchants):
 *
 *   NULL   11,159 machines   no merchant resolved → default
 *   1       5,054            the Portmone bulk → default
 *   2         648            TBC — 646 of them Georgian companies billing in GEL,
 *                            one literally named "TBC_Payment"
 *   3         212            Kaspi — JDE_KZ, KZT. Not matched by this map, so
 *                            these machines fall through to the default. Whether
 *                            that is intended is an open product question.
 *   4               0        the id the old TBC constant claimed
 *   5               0        MyPOS — no live machines
 *   6          17            EMerchantPay, EUR
 *   8           3            MiaPay, MDL
 *
 * web0 never reads `provider_id` at all — it is a free-text admin field there, with
 * no enum, validator or lookup. The meaning is enforced only by the webQR backend
 * that aliases it as `payment_provider_id`, and by this file.
 *
 * Names dropped rather than kept: MASTERPASS (0), GOOGLE_PAY (1) and APPLE_PAY (2)
 * are `payment_system` names, and as provider ids they are simply wrong — 1 is the
 * Portmone bulk and 2 is TBC. Keeping a wrong name is worse than having none; use
 * web0's enum if you need those.
 */
export const PAYMENT_PROVIDER = {
    TBC: 2,
    KASPI: 3,
    MYPOS: 5,
    EMERCHANTPAY: 6,
    TRANZZO: 7,
    MIAPAY: 8,
} as const;

export const AVAILABLE_WATER_VOLUMES = [5, 6, 10, 12, 19];

export const AVAILABLE_CT5_VM_TYPES: VendingMachineTypeId[] = [
    MACHINE_TYPES.SNACK_MACHINE,
    MACHINE_TYPES.COFFEE_AUTOMAT,
    MACHINE_TYPES.SNACK_MACHINE_SPARKA,
    MACHINE_TYPES.COFFEE_MACHINE_SPARKA,
    MACHINE_TYPES.COFFEE_MACHINE,
    MACHINE_TYPES.FRESH_MACHINE,
    MACHINE_TYPES.ICE_CREAM_MACHINE,
    MACHINE_TYPES.CIGARETTE_MACHINE,
    MACHINE_TYPES.CONDOM_MACHINE,
    // SWRN-1893: pulse machines move to ct_type = 5, so the credit is sent after
    // payment and for the amount paid instead of the machine credit ceiling
    ...PULSE_MACHINE_TYPES,
];


// permission for service/payment pages
export const QR_SERVICES = {
    NOTHING_IS_ALLOWED: 0,
    ONLY_PAYMENT: 1,
    SEPARATED_SERVICES: 2,
    ALL_IS_PERMITTED: 3,
};

const PAYMENT_ERROR = {
    TRANSACTION_DECLINED: '1',
    ACQUIRER_PROBLEM: '2',
    BANK_PROBLEM: '3',
    TECHNICAL_PROBLEM: '4',
    LIMIT_PROBLEM: '5',
    LOW_BALANCE: '6',
    INVALID_CVV: '7',
};

export const ERROR_MESSAGES: { [key: string]: string } = {
    [PAYMENT_ERROR.TRANSACTION_DECLINED]: 'Transaction declined',
    [PAYMENT_ERROR.ACQUIRER_PROBLEM]: 'Acquiring bank problem',
    [PAYMENT_ERROR.BANK_PROBLEM]: 'Bank issue',
    [PAYMENT_ERROR.TECHNICAL_PROBLEM]: 'Technical problems',
    [PAYMENT_ERROR.LIMIT_PROBLEM]: 'Limit problem',
    [PAYMENT_ERROR.LOW_BALANCE]: 'Low balance',
    [PAYMENT_ERROR.INVALID_CVV]: 'Invalid CVV',
};


export const API = {
    CHECK_QR: IS_DEV ? '/dev/qr.json' : '/api/check',
    ORDER: '/api/order',
    ORDER_AMOUNT: IS_DEV ? '/dev/order_paid.json' : '/api/order-amount',
    ORDER_GOOGLE_PAYMENT: IS_DEV ? '/dev/order_paid.json' : '/api/paymentGoogle',
    ORDER_MYPOS_PAYMENT: IS_DEV ? '/dev/order_paid.json' : '/api/paymentMyPos',
    ORDER_EMERCHANT_PAYMENT: IS_DEV
        ? '/dev/order_paid.json'
        : '/api/paymentEmerchant',
    VALIDATE_APPLE_MERCHANT: '/api/appleValidateMerchant',
    LEGAL_UPDATE: '/api/get_legal_update',
    FEEDBACK_DATA: '/api/get_feedback_data',
    CONNECT: '/api/connect',
    CANCEL: '/api/cancel',
    RECEIPT: '/api/receipt',
};
export const NEW_PRIVACY_POLICY_URL = 'https://docs.prostopay.net';
export const SOFTWARE_VERSION_SIX_TIMER = IS_LOCAL_DEVELOPMENT
    ? { CONNECT: 3000, SELECT: 2999 }
    : { CONNECT: 30, SELECT: 29 };
export const SUBUNITS_IN_CURRENCY = 100; // cents or копійки

const getTimerStartValue = () => {
    if (IS_LOCAL_DEVELOPMENT) return 999;
    if (IS_DEV) return 50;
    return 90;
};

export const TIMER_START_VALUE = getTimerStartValue();
export const DEV_TIMER_DELAY = 3;

/**
 * Fiscal receipt codes. Two columns share this table and their values overlap
 * (`-1` means "deferred" in `fiscal_req` but "PRRO inactive" in
 * `fiscal_success_ID`), so each entry names its column. The in-flight codes
 * mirror payment-service's `FiscalReq*` constants (`internal/domain/order.go`).
 */
export const FISCAL_SET = {
    /** `fiscal_req`: the PRRO issued a receipt. */
    SUCCESS: 200,
    /** `fiscal_success_ID` marker: the PRRO did not answer in time. */
    TIMEOUT: -3,
    /** `fiscal_req`: no receipt filed (PRRO inactive or VAT exempt). */
    FISCALISATED: 1,
    /** `fiscal_req`: no receipt needed — or, with `TIMEOUT`, a PRRO timeout. */
    NOT_REQUIRED: 0,
    /** `fiscal_req`: deferred until the machine reports what it dispensed (CT5). */
    DEFERRED: -1,
    /** `fiscal_req`: a report claimed the deferred receipt. */
    CONFIRMING: -4,
    /** `fiscal_req`: the receipt is being issued. */
    ISSUING: -5,
};

// hunter-dev limitations
export const DEV_MACHINES = [
    '600444',
    '999997',
    '989001',
    '600318',
    '160001',
    '664422',
    'MD100001',
    'MD100002',
    '700010',
    '700011',
];

